import { notificationSuccess } from 'app/core/toast';
import { AxiosError } from 'axios';
import { type ResponseValues } from 'axios-hooks';
import i18next from 'i18next';
import { useNavigate } from 'react-router-dom';

import { BaseUrlTaxpayer } from '@/app/modules/taxes/services/tax.service';

import { useGetApplication } from '../../../hooks';
import { useCatalogValueList } from '../../configuration/catalogs/hooks/useCatalogValueList';
import { type CatalogModel } from '../../configuration/catalogs/models/Catalog';
import { messages, messagesKey } from '../../translation';
import { useTaxpayerRequestStore } from '../stores/useTaxpayerRequestStore';

import {
  base,
  saveTaxypayerRequest,
  updateTaxypayerRequest
} from './services/taxpayerRequest.service';
import { isEnabled } from './enabled';
import {
  type EnrollmentTaxPayerInfo,
  type SaveRequest,
  type Tax,
  type Taxpayers,
  type UpdateRequest
} from './types';

export const borrar = () => {};
export const useGetTaxpayerRequest = (id: string) => {
  const result = useGetApplication<any>({
    url: `${base}/` + id,
    params: undefined
  });

  return result;
};

export const useGetTaxpayerRequestByTaxpayer = (id: string) => {
  const result = useGetApplication<any>({
    url: `${base}/taxpayer/` + id,
    params: undefined
  });
  return result;
};

interface Value {
  id: string;
  name: string;
  filingDueDay: number;
}
export const useGetTaxPeriodicities = (
  taxId: string,
  onLoad = true,
  ignoreDefaultLang?: boolean
): ResponseValues<Array<Value>, any, any> => {
  const url = `/taxes/${taxId}/TaxPeriodicity/All`;
  const [{ data, loading, error }] = useGetApplication<Array<any>>(
    {
      url: url,
      params: undefined,
      headers: ignoreDefaultLang ? { lang: true } : undefined
    },
    onLoad
  );

  return {
    data: data?.map((x) => {
      return {
        id: x.id,
        name: x.name,
        filingDueDay: x.filingDueDay
      };
    }),
    error,
    loading
  };
};

export const useGetReportPeriodicities = (
  reportId: string,
  onLoad = true,
  ignoreDefaultLang?: boolean
): ResponseValues<Array<Value>, any, any> => {
  const url = `/reports/${reportId}/ReportPeriodicity/All`;
  const [{ data, loading, error }] = useGetApplication<Array<any>>(
    {
      url: url,
      params: undefined,
      headers: ignoreDefaultLang ? { lang: true } : undefined
    },
    onLoad
  );

  return {
    data: data?.map((x) => {
      return {
        id: x.id,
        name: x.periodicityType?.name,
        filingDueDay: x.filingDueDay
      };
    }),
    error,
    loading
  };
};

export const useGetTaxByOperations = (
  operations: Array<string>,
  onLoad = true,
  ignoreDefaultLang?: boolean
) => {
  const response = useGetApplication<Array<Tax>>(
    {
      url: `taxes/All`,
      params: { operations },
      headers: ignoreDefaultLang ? { lang: true } : undefined
    },
    onLoad
  );

  return response;
};

export const useGetReportByOperations = (
  operations: Array<string>,
  onLoad = true,
  ignoreDefaultLang?: boolean
) => {
  const response = useGetApplication<Array<Tax>>(
    {
      url: `reports/All`,
      params: { operations },
      headers: ignoreDefaultLang ? { lang: true } : undefined
    },
    onLoad
  );

  return response;
};

export const useGetFilesDefinitionTaxpayerRequest: (
  ignoreDefaultLang?: boolean
) => ResponseValues<Array<CatalogModel>, any, any> = () => {
  const { data, error, isLoading: loading } = useCatalogValueList('files-definition');
  const result = data?.filter((x) => isEnabled(x.additionalFields.enable)) || [];

  return { data: result, error: error ? AxiosError.from(error) : null, loading };
};

export const useTaxpayerRequestCapabilities = (ignoreDefaultLang = false) => {
  const [{ data: reports, loading: loadingReports, error: reportsError }] =
    useGetReportByOperations([], true, ignoreDefaultLang);
  const {
    data: fileDefinitions,
    loading: loadingFiles,
    error: filesError
  } = useGetFilesDefinitionTaxpayerRequest(ignoreDefaultLang);
  const {
    data: contactFileDefinitions,
    isLoading: loadingContactFiles,
    error: contactFilesError
  } = useCatalogValueList('contact-files-definition');

  return {
    loading: loadingReports || loadingFiles || loadingContactFiles,
    error: reportsError || filesError || contactFilesError,
    reportsEnabled: (reports?.length ?? 0) > 0,
    attachmentsEnabled:
      (fileDefinitions?.length ?? 0) > 0 ||
      (contactFileDefinitions?.some((definition) =>
        isEnabled(definition.additionalFields.enable)
      ) ??
        false)
  };
};

export const useGetTaxPayers: () => ResponseValues<Taxpayers, any, any> = () => {
  const [{ data, error, loading }] = useGetApplication<Taxpayers>({
    url: `${BaseUrlTaxpayer}`,
    params: undefined
  });
  return { data: data, error, loading };
};

export const useTaxpayerRequestActions = () => {
  const navigate = useNavigate();
  const reset = useTaxpayerRequestStore((s) => s.reset);

  const saveRequest = async (payload: SaveRequest) => {
    const request = {
      enrollmentTaxPayerInfo: {
        ...payload.data,
        preferredLanguage: i18next.language
      }
    } as EnrollmentTaxPayerInfo;

    try {
      await saveTaxypayerRequest(request);
      notificationSuccess(messages(messagesKey.savedSucessfully));
      reset();
      navigate(payload.redirectMeta.path);
    } catch {
      return;
    }
  };

  const updateRequest = async (payload: UpdateRequest) => {
    const request = {
      enrollmentTaxPayerInfo: {
        ...payload.data,
        preferredLanguage: i18next.language
      }
    } as EnrollmentTaxPayerInfo;

    try {
      await updateTaxypayerRequest(payload.id, request);
      notificationSuccess(messages(messagesKey.savedSucessfully));
      reset();
      if (typeof payload.redirectMeta.path === 'number') {
        navigate(payload.redirectMeta.path);
      } else {
        navigate(payload.redirectMeta.path);
      }
    } catch {
      return;
    }
  };

  return { saveRequest, updateRequest };
};
